/**
 * Экран «Награды» · Ф2.4 — плотный список со шкалами (характер «дата-про», выбор 24.07).
 *
 * Данные — `GET /achievements` (движок `core/achievements.py`): у каждой награды своё
 * текущее значение, порог и признак «получена». Витрина ничего не пересчитывает.
 *
 * ⭐ Полученные и будущие стоят РАЗДЕЛЬНО: открытые — с датой, будущие — по близости,
 * у каждой шкала и «сколько осталось». Награда кликабельна: лист-деталь говорит,
 * как её получить и сколько до неё (принятый вид прототипа).
 *
 * ⭐ Ф2.5: блок «что даёт» стал НАСТОЯЩИМ. Опыт в приложении появился (слой 3), и его
 * цену за награду присылает ядро (`/achievements` → `xp_reward` = `gamify.XP_ACHIEVEMENT`),
 * а не выдумывает экран. Монеток по-прежнему нет — и мы про них молчим, а не обещаем.
 *
 * Тексты и раскладка живут в `src/awards.ts` и проверяются запуском:
 *   cd Lang/web && node tools/check-screens.mjs
 */

import "../styles/awards.css";

import {
  counter, earnedDate, howTo, icon, leftLabel, nearest, pct, plural, split,
  type AchEarned, type AchProgress,
} from "../awards";
import { state } from "../state";
import { tap } from "../tg";
import { rewardLabel } from "../xp";
import { refreshTogether, renderTogether } from "./together";

/**
 * Окно 4: «Вместе» — вторая вкладка ЭТОГО экрана, а не шестой пункт навигации.
 * Так решили на дизайн-воротах (навигация — пять пунктов) и так же было в принятом
 * прототипе Ф2.0 («Награды · Вместе · Магазин» — вкладки одного экрана).
 */
type Tab = "awards" | "together";
let tab: Tab = "awards";

const TAB_LABEL: [Tab, string][] = [["awards", "Награды"], ["together", "Вместе"]];

function tabsHtml(): string {
  return '<div class="lbtabs top" role="group" aria-label="Разделы">' +
    TAB_LABEL.map(([key, label]) =>
      `<button type="button" data-atab="${key}" aria-pressed="${key === tab}">${label}</button>`)
      .join("") + "</div>";
}

export function renderAwards(host: HTMLElement): void {
  host.innerHTML = tabsHtml();
  const body = document.createElement("div");
  host.appendChild(body);
  host.querySelector(".lbtabs")?.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("button[data-atab]");
    if (!btn) return;
    tap(btn);
    const next = btn.dataset.atab as Tab;
    const changed = next !== tab;
    tab = next;
    renderAwards(host);
    if (changed && tab === "together") refreshTogether();   // открыли — перечитали
  });
  if (tab === "together") renderTogether(body);
  else renderAwardsBody(body);
}

const num = (n: number) => n.toLocaleString("ru-RU");

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"']/g, (ch) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch] as string));
}

/** Описание из базы — оно приезжает только у начисленных наград (`earned`). */
function earnedIndex(list: AchEarned[]): Map<string, AchEarned> {
  const map = new Map<string, AchEarned>();
  for (const e of list || []) map.set(e.code, e);
  return map;
}

function row(a: AchProgress, got: Map<string, AchEarned>): string {
  const e = got.get(a.code);
  const sub = a.earned
    ? (e ? `${esc(e.description)} · получено ${esc(earnedDate(e.earned_at))}` : "Открыта")
    : esc(leftLabel(a));
  const bar = a.earned ? "" : `<div class="pt"><i style="width:${pct(a)}%"></i></div>`;
  return (
    `<button type="button" class="ach${a.earned ? " got" : ""}" data-ach="${esc(a.code)}">` +
      `<div class="ic">${icon(a)}</div>` +
      '<div class="mid">' +
        `<div class="t">${esc(a.title)}${a.earned ? '<span class="chk">✓</span>' : ""}</div>` +
        `<div class="d">${sub}</div>` + bar +
      "</div>" +
      `<div class="rt">${esc(counter(a))}</div><span class="chev">›</span>` +
    "</button>"
  );
}

function renderAwardsBody(host: HTMLElement): void {
  const data = state.achievements;
  if (!data) { host.innerHTML = '<div class="stub"><p>Награды ещё едут…</p></div>'; return; }

  const { earned, upcoming } = split(data.catalog || []);
  const got = earnedIndex(data.earned || []);
  const total = (data.catalog || []).length;
  const near = nearest(data.catalog || []);
  const reward = Number(data.xp_reward ?? 0);      // цена награды в опыте — из ядра

  const openBlock = earned.length
    ? '<div class="card">' +
        `<p class="h">Открыто · ${earned.length} ${plural(earned.length, "награда", "награды", "наград")}</p>` +
        '<p class="hsub">Нажми на любую — расскажем, за что она дана.</p>' +
        earned.map((a) => row(a, got)).join("") +
      "</div>"
    : '<div class="card">' +
        '<p class="h">Пока ни одной</p>' +
        '<p class="hsub">Первая упадёт сама: награды начисляются на занятии, ' +
        "проверять и нажимать ничего не нужно.</p>" +
      "</div>";

  const nextBlock = upcoming.length
    ? '<div class="card">' +
        `<p class="h">Впереди · ${upcoming.length}</p>` +
        '<p class="hsub">Сверху та, до которой ближе всего.</p>' +
        upcoming.map((a) => row(a, got)).join("") +
      "</div>"
    : "";

  const nearBlock = near
    ? '<div class="card">' +
        '<p class="h">Рукой подать</p>' +
        `<p class="hsub">${esc(near.title)} — ${esc(leftLabel(near))}.</p>` +
        `<div class="goalbar"><i style="width:${pct(near)}%"></i></div>` +
        `<div class="goalrow"><span>${num(near.value)} из ${num(near.threshold)}</span>` +
        `<span>${pct(near)}%</span></div>` +
      "</div>"
    : "";

  host.innerHTML =
    '<div class="two-col">' +
      '<div class="col-main">' +
        '<div class="greet"><h2>Награды</h2>' +
        `<span class="streak">🏆 ${earned.length} / ${total}</span></div>` +
        openBlock + nextBlock +
      "</div>" +
      '<div class="col-side">' +
        nearBlock +
        '<p class="footnote">Награды считает движок ядра: он сам сверяет счётчики после ' +
        "каждого ответа и начисляет заслуженное — пропустить награду нельзя." +
        (reward ? ` Каждая добавляет ${esc(rewardLabel(reward))
          .replace(" и отметку в этом списке", "")} к твоему уровню.` : "") + "</p>" +
      "</div>" +
    "</div>";

  host.querySelectorAll<HTMLElement>("[data-ach]").forEach((btn) => {
    btn.addEventListener("click", () => {
      tap(btn);
      openSheet(btn.dataset.ach as string);
    });
  });
}

/* ── лист-деталь: как получить · сколько осталось ─────────────────────────── */

let sheetWrap: HTMLElement | null = null;

/**
 * Лист живёт в оболочке, а НЕ внутри экрана: `.scroll` обрезает всё, что вылезает за
 * его границы, и всплывающий снизу лист там срезало бы вместе с затемнением.
 */
function ensureSheet(): HTMLElement | null {
  if (sheetWrap && sheetWrap.isConnected) return sheetWrap;
  const shell = document.getElementById("shell");
  if (!shell) return null;
  const wrap = document.createElement("div");
  wrap.className = "sheetwrap";
  wrap.innerHTML = '<div class="sheetbg"></div><div class="sheet" role="dialog" aria-modal="true"></div>';
  shell.appendChild(wrap);
  wrap.querySelector(".sheetbg")?.addEventListener("click", closeSheet);
  document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeSheet(); });
  sheetWrap = wrap;
  return wrap;
}

function closeSheet(): void {
  sheetWrap?.classList.remove("on");
}

function openSheet(code: string): void {
  const data = state.achievements;
  const a = (data?.catalog || []).find((x) => x.code === code);
  const wrap = ensureSheet();
  if (!a || !wrap) return;
  const e = earnedIndex(data?.earned || []).get(code);
  const sheet = wrap.querySelector<HTMLElement>(".sheet");
  if (!sheet) return;
  // Цена награды в опыте — из ядра. Нет ключа (старый API) — молчим, а не выдумываем.
  const reward = Number(data?.xp_reward ?? 0);

  const done = a.earned
    ? '<div class="blk"><div class="bh">Уже твоя</div>' +
      `<div class="bt">${e ? esc(e.description) : "Награда открыта"}` +
      `${e ? ` — получено ${esc(earnedDate(e.earned_at))}.` : "."}</div></div>`
    : '<div class="blk"><div class="bh">Сколько осталось</div>' +
      `<div class="pt big"><i style="width:${pct(a)}%"></i></div>` +
      `<div class="prow"><span>${num(a.value)} из ${num(a.threshold)}</span>` +
      `<span>${esc(leftLabel(a))}</span></div></div>`;

  sheet.innerHTML =
    '<div class="grab"></div>' +
    `<div class="shead${a.earned ? " got" : ""}"><div class="ic">${icon(a)}</div>` +
      `<div><div class="t">${esc(a.title)}</div>` +
      `<span class="statepill ${a.earned ? "got" : "wip"}">` +
      `${a.earned ? "Открыта" : `В процессе · ${pct(a)}%`}</span></div></div>` +
    `<div class="blk"><div class="bh">Как получить</div><div class="bt">${esc(howTo(a))}</div></div>` +
    done +
    '<div class="blk"><div class="bh">Что даёт</div>' +
      `<div class="bt">${reward
        ? `${esc(rewardLabel(reward))}${a.earned ? " — уже начислено." : "."}`
        : "Саму отметку в этом списке."}</div></div>` +
    '<button class="sheetclose" type="button">Закрыть</button>';

  sheet.querySelector(".sheetclose")?.addEventListener("click", closeSheet);
  wrap.classList.add("on");
}
